import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { getDataProtectionFeatures } from './landingContent';
import {
  SuccessValuesIcon,
  MissionIcon,
  DataProtectionIcon
} from './icons';

/**
 * DataProtectionSection - Data protection and customer support section
 * 
 * Features:
 * - Data protection features display
 * - Customer support information
 * - Responsive design
 * - Icon and content layout
 * 
 * @returns {JSX.Element} The data protection section component
 */
export const DataProtectionSection: React.FC = () => {
  const { t } = useTranslation('marketing');
  const dataProtectionFeatures = useMemo(() => getDataProtectionFeatures(t), [t]);

  const getIconComponent = (iconId: string) => {
    switch (iconId) {
      case 'success-values':
        return <SuccessValuesIcon />;
      case 'mission':
        return <MissionIcon />;
      case 'data-protection':
        return <DataProtectionIcon />;
      default:
        return null;
    }
  };

  return (
    <section
      className="flex flex-col items-center w-full bg-white px-4 sm:px-6 py-8 sm:py-12 lg:py-16"
      {...getGTMAttributes(createGTMTracking('SECTION_VIEWED', { origin: 'landing_page', target: 'data_protection' }))}
    >
      <div className="flex w-full max-w-screen-2xl mx-auto flex-col justify-center items-center gap-8 sm:gap-10 lg:gap-12 bg-background-gray p-6 sm:p-10 lg:p-20 rounded-2xl sm:rounded-3xl lg:rounded-4xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col items-center gap-4 sm:gap-[18px] w-full py-0"
        >
          <h2 className="w-full text-dark-invirtus text-center text-2xl sm:text-3xl lg:text-5xl font-bold leading-tight">
            {t('data_protection.title_line1')} <br className="hidden sm:inline" /> {t('data_protection.title_line2')}
          </h2>
        </motion.div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 lg:gap-7 w-full">
          {dataProtectionFeatures.map((feature, index) => (
            <motion.article
              key={feature.id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.4, delay: 0.05 + (index * 0.05) }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="flex h-full w-full flex-col items-center gap-4 sm:gap-6 lg:gap-8 bg-white p-4 sm:p-6 lg:p-8 rounded-2xl"
            >
              <div className="flex-shrink-0 mb-6 lg:mb-2">
                {getIconComponent(feature.iconId)}
              </div>
              <div className="flex flex-col items-start gap-3 sm:gap-4 lg:gap-[18px] w-full">
                <div className="flex items-baseline gap-1 w-full">
                  <h3 className="w-full text-dark-invirtus text-center text-xl sm:text-2xl lg:text-3xl font-bold">
                    {feature.title}
                  </h3>
                </div>
                <p className="w-full px-6 lg:px-8 pb-4 overflow-hidden text-dark-invirtus/70 text-center text-ellipsis text-xs sm:text-sm font-normal">
                  {feature.description}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
